import { ValueEventBox, ValueEventCollectionBox } from "@opendaw/studio-boxes";
import { asDefined, Curve, linear, Notifier, Option, panic, Terminator, UUID } from "@opendaw/lib-std";
import { EventCollection, Interpolation, ValueEvent } from "@opendaw/lib-dsp";
import { Pointers } from "@opendaw/studio-enums";
import { ValueEventBoxAdapter } from "../event/ValueEventBoxAdapter";
import { InterpolationFieldAdapter } from "../event/InterpolationFieldAdapter";
export class ValueEventCollectionBoxAdapter {
    #terminator = new Terminator();
    #context;
    #box;
    #changeNotifier;
    #adapters;
    #events;
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
        this.#changeNotifier = new Notifier();
        this.#adapters = UUID.newSet(adapter => adapter.uuid);
        this.#events = EventCollection.create(ValueEventBoxAdapter.Comparator);
        const addValueProcedure = (box) => {
            const adapter = asDefined(box.accept({
                visitValueEventBox: (box) => this.#context.boxAdapters.adapterFor(box, ValueEventBoxAdapter)
            }), `Could not find adapter for ${box}`);
            if (this.#adapters.add(adapter)) {
                this.#events.add(adapter);
                this.#onEventsChanged();
            }
        };
        this.#box.events.pointerHub.incoming().forEach(({ box }) => addValueProcedure(box));
        this.#terminator.own(this.#box.events.pointerHub.subscribe({
            onAdded: ({ box }) => addValueProcedure(box),
            onRemoved: ({ box: { address: { uuid } } }) => {
                this.#events.remove(this.#adapters.removeByKey(uuid));
                this.#onEventsChanged();
            }
        }));
        this.#terminator.own(this.#box.owners.pointerHub.subscribe({
            onAdded: () => this.#changeNotifier.notify(this),
            onRemoved: () => this.#changeNotifier.notify(this)
        }));
    }
    valueAt(position, fallback) { return ValueEvent.valueAt(this.#events, position, fallback); }
    copy() {
        const graph = this.#context.boxGraph;
        const boxCopy = ValueEventCollectionBox.create(graph, UUID.generate());
        this.#events.asArray().forEach(source => source.copyTo({ events: boxCopy.events }));
        return this.#context.boxAdapters.adapterFor(boxCopy, ValueEventCollectionBoxAdapter);
    }
    cut(position) {
        const low = this.events.lowerEqual(position);
        const high = this.events.greaterEqual(position);
        if (null === high) {
            if (null === low) {
                return Option.None;
            }
            return Option.wrap(this.createEvent({
                position,
                value: low.value,
                index: low.index,
                interpolation: low.interpolation
            }));
        }
        if (null === low) {
            return Option.wrap(this.createEvent({
                position,
                value: high.value,
                index: high.index,
                interpolation: high.interpolation
            }));
        }
        if (low.position === position) {
            return Option.wrap(low);
        }
        if (low.interpolation.type === "none") {
            return Option.wrap(this.createEvent({
                position,
                value: low.value,
                index: low.index,
                interpolation: low.interpolation
            }));
        }
        if (low.interpolation.type === "linear") {
            const { position: p0, value: v0 } = low;
            const { position: p1, value: v1 } = high;
            return Option.wrap(this.createEvent({
                position,
                value: linear(v0, v1, (position - p0) / (p1 - p0)),
                index: 0,
                interpolation: low.interpolation
            }));
        }
        if (low.interpolation.type === "curve") {
            const { position: p0, value: y0 } = low;
            const { position: p1, value: y1 } = high;
            const steps = p1 - p0;
            const cutOffset = position - p0;
            const curve = Curve.byHalf(steps, y0, Curve.valueAt({
                slope: low.interpolation.slope,
                steps,
                y0,
                y1
            }, steps * 0.5), y1);
            const cutValue = Curve.valueAt(curve, cutOffset);
            const lowSlope = Curve.slopeByHalf(y0, Curve.valueAt(curve, cutOffset * 0.5), cutValue);
            InterpolationFieldAdapter.write(low.box.interpolation, Interpolation.Curve(lowSlope));
            return Option.wrap(this.createEvent({
                position,
                value: cutValue,
                index: 0,
                interpolation: Interpolation.Curve(Curve.slopeByHalf(cutValue, Curve.valueAt(curve, (cutOffset + steps) * 0.5), y1))
            }));
        }
        return panic("Unknown interpolation type");
    }
    subscribeChange(observer) { return this.#changeNotifier.subscribe(observer); }
    createEvent({ position, index, value, interpolation }) {
        const eventBox = ValueEventBox.create(this.#context.boxGraph, UUID.generate(), box => {
            box.position.setValue(position);
            box.index.setValue(index);
            box.value.setValue(value);
            box.events.refer(this.#box.events);
        });
        InterpolationFieldAdapter.write(eventBox.interpolation, interpolation);
        return this.#context.boxAdapters.adapterFor(eventBox, ValueEventBoxAdapter);
    }
    requestSorting() {
        this.#events.onIndexingChanged();
        this.onEventPropertyChanged();
    }
    onEventPropertyChanged() { this.#onEventsChanged(); }
    terminate() { this.#terminator.terminate(); }
    get box() { return this.#box; }
    get uuid() { return this.#box.address.uuid; }
    get address() { return this.#box.address; }
    get numOwners() { return this.#box.owners.pointerHub.filter(Pointers.ValueEventCollection).length; }
    get events() { return this.#events; }
    toString() { return `{ValueEventCollectionBox ${UUID.toString(this.#box.address.uuid)}}`; }
    #onEventsChanged() { this.#changeNotifier.notify(this); }
}
